import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdbModalRef, MdbModalService } from 'mdb-angular-ui-kit/modal';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { CONFIGURATION_CONSTANT, NIGHTWEB_APP_CONSTANT } from 'src/app/constants';
import { InputType, UnitType } from 'src/app/enums';
import { Configuration, WatchTheme, WatchThemeStatus } from 'src/app/models';
import { DataService, OrganizationService, ToasterService, UserService, WatchThemeService } from 'src/app/services';
import { NightwebUtil } from 'src/app/utils';
import { LinkDeviceModalComponent } from '../link-device-modal/link-device-modal.component';
import { LinkUserModalComponent } from '../link-user-modal/link-user-modal.component';
import { AddEditUserModalComponent } from '../../modals';

@Component({
    selector: 'app-configuration-modal',
    templateUrl: './configuration-modal.component.html',
    styleUrls: ['./configuration-modal.component.scss'],
    standalone: false
})
export class ConfigurationModalComponent implements OnInit {
  loading = false;
  submitted = false;
  title!: string;
  formData: any = {};
  inputType = InputType;
  unitType = UnitType;
  configurationForm!: FormGroup;
  confirmButtonLabel: string = '';
  organization: any;
  percentageModel!: number;
  checkInTimeModel!: number;
  timeConversionValues = [
    CONFIGURATION_CONSTANT.CHECK_IN_TIME,
    CONFIGURATION_CONSTANT.EMERGENCY_CHECK_IN_TIME,
    CONFIGURATION_CONSTANT.NO_PACKET_THRESHOLD,
    CONFIGURATION_CONSTANT.NOT_WORN_THRESHOLD,
    CONFIGURATION_CONSTANT.MISSING_GPS_CONSECUTIVE_COOLDOWN,
    CONFIGURATION_CONSTANT.ADMIN_ESCALATION_DELAY,
  ];
  invalidFields: any[] = [];
  phoneMask = NIGHTWEB_APP_CONSTANT.phoneMask;
  readonly CONFIGURATION_CODE = CONFIGURATION_CONSTANT;
  private readonly defaultRapidResponsePhone = '8442263741';

  config = NIGHTWEB_APP_CONSTANT.modalConfig;
  linkDeviceComponent!: MdbModalRef<LinkDeviceModalComponent>;
  linkUserComponent!: MdbModalRef<LinkUserModalComponent>;
  availableThemes: WatchTheme[] = [];
  excludedThemeNames: string[] = [];

  constructor(
    private dataService: DataService,
    private formBuilder: FormBuilder,
    private userService: UserService,
    private nightWebUtil: NightwebUtil,
    private modalService: MdbModalService,
    private toasterService: ToasterService,
    private organizationService: OrganizationService,
    private watchThemeService: WatchThemeService,
    private _userService: UserService,
    public modalRef: MdbModalRef<ConfigurationModalComponent>
  ) {}

  get configurations(): FormArray {
    return this.configurationForm.get('configurations') as FormArray;
  }

  /** Set after POST create org: user must complete Update (Edit Configuration leaves this unset). */
  get dismissControlsDisabled(): boolean {
    return !!this.formData?.disableDismissControls;
  }

  ngOnInit(): void {
    this.setConfigurationForm();
  }

  get isOrganizationModule(): boolean {
    return this.formData?.module === 'organization';
  }

  private catalogThemes$() {
    /*return this.watchThemeService.getAvailable().pipe(
      catchError(() => of({ data: { rows: [] } })),
    );*/
    return of({ data: { rows: [] } });
  }

  private catalogAll$() {
    /*return this.watchThemeService
      .getAll({
        limit: 100,
        offset: NIGHTWEB_APP_CONSTANT.defaultOffset,
      })
      .pipe(catchError(() => of({ data: { rows: [] } })));*/
      return of({ data: { rows: [] } });
  }

  private setThemeLists(availableRows: WatchTheme[], catalogRows?: WatchTheme[]) {
    this.availableThemes = availableRows || [];
    const catalog = catalogRows || [];
    this.excludedThemeNames = catalog
      .filter(
        (theme) =>
          theme.status === WatchThemeStatus.PENDING_BUILD ||
          theme.status === WatchThemeStatus.RETIRED,
      )
      .map((theme) => theme.name);
  }

  setConfigurationForm() {
    const { deviceId, userId, organizationId, mode, module } = this.formData;
    this.configurationForm = this.formBuilder.group({
      deviceId: [deviceId, Validators.required],
      endUserId: [userId, Validators.required],
      configurations: this.formBuilder.array([]),
    });
    if (module === 'user') {
      this.loading = true;
      this.userService
        .getUserById(this.formData.userId)
        .pipe(
          switchMap((user) => {
            const organizationId = user.data?.organizationId || this.formData.organizationId;
            const hasTheme = (user.data?.configurations || []).some(
              (configuration: any) =>
                configuration.code === CONFIGURATION_CONSTANT.WATCH_THEME,
            );
            return forkJoin({
              user: of(user),
              available: this.catalogThemes$(),
              catalog: this.catalogAll$(),
              organization:
                !hasTheme && organizationId
                  ? this.organizationService.getOrganizationById(organizationId).pipe(
                      catchError(() => of({ data: { configurations: [] } })),
                    )
                  : of({ data: { configurations: [] } }),
            });
          }),
          finalize(() => (this.loading = false)),
        )
        .subscribe({
          next: ({ user, available, catalog, organization }) => {
            this.formData.configurations = user.data.configurations;
            this.formData.organizationId = user.data.organizationId || this.formData.organizationId;
            this.organization = organization.data;
            this.setThemeLists(available.data?.rows || [], catalog.data?.rows || []);
            this.percentageModel = user.data.configurations.find(
              (configuration: any) =>
                configuration.code ===
                CONFIGURATION_CONSTANT.LOW_BATTERY_THRESHOLD
            )?.value;
            this.checkInTimeModel = user.data.configurations.find(
              (configuration: any) =>
                configuration.code === CONFIGURATION_CONSTANT.CHECK_IN_TIME
            )?.value;
            this.setConfigurations();
          },
          error: (e) => {},
        });
    } else if (module === 'organization') {
      this.loading = true;
      forkJoin({
        organization: this.organizationService.getOrganizationById(this.formData.organizationId),
        available: this.catalogThemes$(),
        catalog: this.catalogAll$(),
      })
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: ({ organization, available, catalog }) => {
            this.organization = organization.data;
            this.formData.configurations = organization.data.configurations;
            this.setThemeLists(available.data?.rows || [], catalog.data?.rows || []);
            this.percentageModel = organization.data.configurations.find(
              (configuration: any) =>
                configuration.code ===
                CONFIGURATION_CONSTANT.LOW_BATTERY_THRESHOLD
            )?.value;
            this.checkInTimeModel = organization.data.configurations.find(
              (configuration: any) =>
                configuration.code === CONFIGURATION_CONSTANT.CHECK_IN_TIME
            )?.value;
            this.setConfigurations();
          },
          error: (e) => {},
        });
    }
  }
 
  setConfigurations() {
    const configurations = this.nightWebUtil.shallowCopy(
      this.formData?.configurations || []
    );
    this.ensureWatchThemeConfiguration(configurations);
    //this.ensureAdminEscalationConfiguration(configurations);
    configurations.map((configuration: any) => {
      if (this.timeConversionValues.includes(configuration.code)) {
        configuration.value = Number(configuration.value)
          ? configuration.value / 60000
          : null;
        if (
          this.isHourTimeField(configuration.code) &&
          configuration.value != null
        ) {
          configuration.value = configuration.value / 60;
        }
      }
      if (
        configuration.code ===
          CONFIGURATION_CONSTANT.RAPID_RESPONSE_CONTACT_PHONE &&
        this.isRapidResponsePhoneValueEmpty(configuration.value)
      ) {
        configuration.value = this.defaultRapidResponsePhone;
      }
      this.configurations.push(this.createItem(configuration));
    });
    this.lockInheritedWatchTheme();
    //this.bindAdminEscalationDelayToEnabled();
    this.checkRequiredValidation();
  }

  shouldShowConfiguration(config: FormGroup | any): boolean {
    const code = config.get('code')?.value;
    if (code === CONFIGURATION_CONSTANT.WATCH_THEME) {
      return this.availableThemes.length > 0;
    }
    if (
      code === CONFIGURATION_CONSTANT.ADMIN_ESCALATION_ENABLED ||
      code === CONFIGURATION_CONSTANT.ADMIN_ESCALATION_DELAY
    ) {
      return false;
    }
    return true;
  }

  private isAdminEscalationEnabled(): boolean {
    const enabled = this.findConfigurationControl(
      CONFIGURATION_CONSTANT.ADMIN_ESCALATION_ENABLED,
    );
    return !!enabled?.get('value')?.value;
  }

  private findConfigurationControl(code: string) {
    return this.configurations.controls.find(
      (control) => control.get('code')?.value === code,
    );
  }

  /** Show/require delay only while Admin Escalation Enabled is on. */
  private bindAdminEscalationDelayToEnabled() {
    const enabledValue = this.findConfigurationControl(
      CONFIGURATION_CONSTANT.ADMIN_ESCALATION_ENABLED,
    )?.get('value');
    const delayValue = this.findConfigurationControl(
      CONFIGURATION_CONSTANT.ADMIN_ESCALATION_DELAY,
    )?.get('value');
    if (!enabledValue || !delayValue) {
      return;
    }

    const syncDelay = (enabled: boolean) => {
      if (enabled) {
        delayValue.enable({ emitEvent: false });
        delayValue.setValidators([Validators.required]);
      } else {
        delayValue.clearValidators();
        delayValue.disable({ emitEvent: false });
      }
      delayValue.updateValueAndValidity({ emitEvent: false });
    };

    syncDelay(!!enabledValue.value);
    enabledValue.valueChanges.subscribe((enabled) => syncDelay(!!enabled));
  }

  /**
   * Org add/edit: WATCH_THEME lives in `configurations` (copied onto users created in that org).
   * User/device: show the inherited row read-only; seed from org if the user payload has none.
   */
  private ensureWatchThemeConfiguration(configurations: any[]) {
    const existingIndex = configurations.findIndex(
      (configuration: any) => configuration.code === CONFIGURATION_CONSTANT.WATCH_THEME
    );
    if (existingIndex > 0) {
      const [watchTheme] = configurations.splice(existingIndex, 1);
      configurations.unshift(watchTheme);
    }
    if (existingIndex >= 0) {
      if (!this.isOrganizationModule) {
        configurations[0] = { ...configurations[0], isLocked: true };
      }
      return;
    }
    if (!this.availableThemes.length) {
      return;
    }
    const orgTheme = (this.organization?.configurations || []).find(
      (configuration: any) => configuration.code === CONFIGURATION_CONSTANT.WATCH_THEME,
    );
    const defaultKey =
      orgTheme?.value ||
      this.availableThemes.find((theme) => theme.isDefault)?.uniqueKey ||
      this.availableThemes[0]?.uniqueKey ||
      '';
    if (!this.isOrganizationModule && !defaultKey) {
      return;
    }
    configurations.unshift({
      code: CONFIGURATION_CONSTANT.WATCH_THEME,
      title: 'Watch Theme',
      description:
        'The watch face every device in this organization runs. Only themes already shipped in a watch app build are listed.',
      type: InputType.STRING,
      value: defaultKey,
      isLocked: !this.isOrganizationModule,
      metadata: {
        note: 'Sent to devices with the next configuration sync.',
      },
    });
  }

  private lockInheritedWatchTheme() {
    if (this.isOrganizationModule) {
      return;
    }
    const watchTheme = this.configurations.controls.find(
      (control) => control.get('code')?.value === CONFIGURATION_CONSTANT.WATCH_THEME,
    );
    watchTheme?.get('value')?.disable({ emitEvent: false });
  }

  /**
   * Admin Escalation Enabled (boolean) under LOW_BATTERY_THRESHOLD, then
   * Admin Escalation Delay (time, hours — same as NO_PACKET_THRESHOLD) under the enabled toggle.
   */
  private ensureAdminEscalationConfiguration(configurations: any[]) {
    const enabled = this.takeOrDefaultConfiguration(configurations, {
      code: CONFIGURATION_CONSTANT.ADMIN_ESCALATION_ENABLED,
      title: 'Admin Escalation Enabled',
      description:
        'Also notify admin when a Not Worn or No Packet notification is sent to advocate.',
      type: InputType.BOOLEAN,
      value: false,
      metadata: null,
    });
    this.insertConfigurationAfter(
      configurations,
      enabled,
      CONFIGURATION_CONSTANT.LOW_BATTERY_THRESHOLD,
    );

    const delay = this.takeOrDefaultConfiguration(configurations, {
      code: CONFIGURATION_CONSTANT.ADMIN_ESCALATION_DELAY,
      title: 'Admin Escalation Delay',
      description:
        'Time to wait after the advocate is notified before the admnin is also notified. Applies to both Not Worn and No Packet.',
      type: InputType.UNIT,
      // Stored as ms; setConfigurations converts to hours for display.
      value: 1 * 60 * 60000,
      metadata: {
        unitType: {
          type: UnitType.TIME,
          unit: 'Millisecond',
          symbol: 'ms',
        },
      },
    });
    this.insertConfigurationAfter(
      configurations,
      delay,
      CONFIGURATION_CONSTANT.ADMIN_ESCALATION_ENABLED,
    );
  }

  private takeOrDefaultConfiguration(
    configurations: any[],
    defaults: Record<string, unknown>,
  ): any {
    const existingIndex = configurations.findIndex(
      (configuration: any) => configuration.code === defaults['code'],
    );
    if (existingIndex >= 0) {
      const [existing] = configurations.splice(existingIndex, 1);
      return existing;
    }
    return defaults;
  }

  private insertConfigurationAfter(
    configurations: any[],
    configuration: any,
    afterCode: string,
  ) {
    const afterIndex = configurations.findIndex(
      (item: any) => item.code === afterCode,
    );
    if (afterIndex >= 0) {
      configurations.splice(afterIndex + 1, 0, configuration);
    } else {
      configurations.push(configuration);
    }
  }

  isWatchThemeLocked(config: FormGroup | any): boolean {
    return !this.isOrganizationModule || !!config.get('isLocked')?.value;
  }

  themeDisplayName(watchThemeKey: string): string {
    return (
      this.availableThemes.find((row) => row.uniqueKey === watchThemeKey)?.name ||
      watchThemeKey ||
      '—'
    );
  }

  themeHelpText(watchThemeKey: string): string {
    const theme = this.availableThemes.find((row) => row.uniqueKey === watchThemeKey);
    if (theme?.watchAppVersion) {
      return `Current build ${theme.watchAppVersion}. Sent to devices with the next configuration sync.`;
    }
    return 'Sent to devices with the next configuration sync.';
  }

  receiveThemeValue(event: any, config: FormGroup | any) {
    const watchThemeKey = event?.value ?? event ?? '';
    config.patchValue({ value: watchThemeKey });
  }

  private isRapidResponsePhoneValueEmpty(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    return false;
  }

  isHourTimeField(code: string): boolean {
    return (
      code === CONFIGURATION_CONSTANT.NO_PACKET_THRESHOLD ||
      code === CONFIGURATION_CONSTANT.NOT_WORN_THRESHOLD ||
      code === CONFIGURATION_CONSTANT.ADMIN_ESCALATION_DELAY
    );
  }

  /** Warn when No Packet / Not Worn thresholds exceed 24 hours. */
  isOver24HourThreshold(config: FormGroup | any): boolean {
    const code = config.get('code')?.value;
    if (
      code !== CONFIGURATION_CONSTANT.NO_PACKET_THRESHOLD &&
      code !== CONFIGURATION_CONSTANT.NOT_WORN_THRESHOLD
    ) {
      return false;
    }
    const hours = parseFloat(String(config.get('value')?.value ?? ''));
    return Number.isFinite(hours) && hours > 24;
  }

  /** Minute-based time fields (stored as ms, shown with `mins` suffix). */
  isMinuteTimeField(code: string): boolean {
    return (
      code === CONFIGURATION_CONSTANT.CHECK_IN_TIME ||
      code === CONFIGURATION_CONSTANT.EMERGENCY_CHECK_IN_TIME ||
      code === CONFIGURATION_CONSTANT.MISSING_GPS_CONSECUTIVE_COOLDOWN
    );
  }

  /** Fixed unit label shown beside time inputs (`hours` / `mins`). */
  timeFieldSuffix(config: FormGroup | any): string {
    const code = config.get('code')?.value;
    if (this.isHourTimeField(code)) {
      return 'hours';
    }
    const symbol = config.get('metadata')?.value?.unitType?.symbol;
    if (symbol === 'ms' || this.isMinuteTimeField(code)) {
      return 'minutes';
    }
    if (symbol === 's') {
      return 'secs';
    }
    return 'hours';
  }

  /** Parses minutes from mask values like "0.5" or "0.5m". */
  private minutesToMs(value: unknown): number {
    const minutes = parseFloat(String(value ?? ''));
    return Number.isFinite(minutes) && minutes > 0 ? minutes * 60000 : 2000;
  }

  /** No Packet / Not Worn thresholds are shown in hours; other time fields stay in minutes. */
  private toStoredTimeMs(configuration: any): any {
    let displayValue = configuration.value;
    if (this.isHourTimeField(configuration.code)) {
      const hours = parseFloat(String(displayValue ?? ''));
      displayValue = Number.isFinite(hours) ? hours * 60 : displayValue;
    }
    return {
      ...configuration,
      value: this.minutesToMs(displayValue),
    };
  }

  createItem(configuration: Configuration): FormGroup {
    return this.formBuilder.group(configuration);
  }

  checkRequiredValidation() {
    if (this.formData.businessName) {
      this.configurationForm.controls['deviceId'].clearValidators();
      this.configurationForm.controls['endUserId'].clearValidators();
    }
    this.configurationForm.controls['deviceId'].updateValueAndValidity();
    this.configurationForm.controls['endUserId'].updateValueAndValidity();
  }

  linkDevice() {
    this.submitted = true;
    this.configurationForm.markAllAsTouched();

    if (this.configurationForm.invalid || this.invalidFields.length > 0) {
      this.toasterService.show(
        'Warning',
        'Please fix the highlighted configuration fields before continuing.'
      );
      return;
    }

    this.loading = true;
    if (this.formData?.mode === 'edit') {
      let data = {
        id: this.formData?.businessName
          ? this.formData.id
          : this.formData?.userId,
        configurations: this.configurations.getRawValue().map(
          (configuration: any) => {
            if (this.timeConversionValues.includes(configuration.code)) {
              return this.toStoredTimeMs(configuration);
            } else {
              return configuration;
            }
          }
        ),
      };
      if (this.formData?.businessName) {
        this.organizationService
          .updateConfigurations(data)
          .pipe(finalize(() => (this.loading = false)))
          .subscribe({
            next: (response) => {
              this.toasterService.show('Success', response.message);
              this.modalRef.close(response.data);
              this.dataService.sendData(response.data);
            },
            error: (e) => {},
          });
      } else {
        this.userService
          .updateConfigurations(data)
          .pipe(finalize(() => (this.loading = false)))
          .subscribe({
            next: (response) => {
              this.toasterService.show('Success', response.message);
              this.modalRef.close(response.data);
              this.dataService.sendData(response.data);
            },
            error: (e) => {},
          });
      }
    } else {
      let data = {
        ...this.configurationForm.value,
        configurations: this.configurations.getRawValue().map(
          (configuration: any) => {
            if (this.timeConversionValues.includes(configuration.code)) {
              return this.toStoredTimeMs(configuration);
            } else {
              return configuration;
            }
          }
        ),
      };
      this.userService
        .linkDevice(data)
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: (response) => {
            this.toasterService.show('Success', response.message);
            this.modalRef.close(response.data);
            this.dataService.sendData(response.data);
          },
          error: (e) => {},
        });
    }
  }

  onHeaderClose(): void {
    if (this.dismissControlsDisabled) {
      return;
    }
    this.modalRef.close();
  }

  cancel() {
    if (this.dismissControlsDisabled) {
      return;
    }
    this.modalRef.close();
    (this.configurationForm.controls['configurations'] as FormArray).clear();
    if (!this.formData?.mode) {
      const config = this.nightWebUtil.shallowCopy(this.config);
      config.modalClass = 'modal-dialog-scrollable modal-md';
      config.data.formData = this.formData;
      if (this.formData?.module === 'Device') {
        config.data.title = 'Link Device';
        this.linkDeviceComponent = this.modalService.open(
          LinkDeviceModalComponent,
          config
        );
      } else if (this.formData?.module === 'User') {
        this.linkUserComponent = this.modalService.open(
          LinkUserModalComponent,
          config
        );
      }
    }
  }

  validateLimit(type: string) {
    switch (type) {
      case 'LOW_BATTERY_THRESHOLD':
        const percentage = this.configurationForm.value.configurations.find(
          (configuration: any) => configuration.code === 'LOW_BATTERY_THRESHOLD'
        );
        if (percentage.value > 100) {
          this.invalidFields.push(type);
        } else {
          const index = this.invalidFields.findIndex((field) => field === type);
          index > -1 && this.invalidFields.splice(index, 1);
        }
        break;
    }
  }
}
